import { Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import Actions from 'actions'
import GrinRadioGroup from 'components/common/GrinRadioGroup'
import PrimaryButton from 'components/common/buttons/PrimaryButton'
import DazzedHeading20 from 'components/common/text/DazzedHeading20'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getPracticeToothNumberingSystem } from 'utils/autochartUtils'

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: -60,
    marginBottom: 60
  },
  sectionHeader: {
    marginBottom: 5
  },
  optionLabel: {
    fontWeight: 'bold',
    fontSize: 14
  }
}))

const AutochartSettings = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const isLoading = useSelector(state => state.profileReducer.isLoading)
  const ownerAppSettingsStr = useSelector(state => state.practiceReducer?.accountOwner?.user?.appSettings)
  const practiceCountry = useSelector(state => state.practiceReducer?.details?.country)

  const ownerAppSettings = useMemo(() => {
    try {
      return JSON.parse(ownerAppSettingsStr || '{}')
    } catch (e) {
      return {}
    }
  }, [ownerAppSettingsStr])

  const savedSystem = ownerAppSettings?.autochartSettings?.toothNumberingSystem
  const effectiveSystem = useMemo(
    () => getPracticeToothNumberingSystem({ appSettings: ownerAppSettings, country: practiceCountry }),
    [ownerAppSettings, practiceCountry]
  )

  const options = useMemo(
    () => [
      {
        value: 'universal',
        label: t('pages.accountSettings.advancedActions.autochartSettings.options.universal', 'Universal')
      },
      { value: 'fdi', label: t('pages.accountSettings.advancedActions.autochartSettings.options.fdi', 'FDI') }
    ],
    [t]
  )

  const [selected, setSelected] = useState(effectiveSystem || 'universal')

  useEffect(() => {
    // If a value was explicitly saved, prefer it; otherwise use the effective default (country fallback)
    setSelected(savedSystem || effectiveSystem || 'universal')
  }, [savedSystem, effectiveSystem])

  const handleSave = useCallback(() => {
    const currentAutochartSettings = ownerAppSettings?.autochartSettings || {}
    dispatch(
      Actions.requestUpdateAppSettings({
        autochartSettings: {
          ...currentAutochartSettings,
          toothNumberingSystem: selected
        }
      })
    )
  }, [dispatch, ownerAppSettings, selected])

  return (
    <Grid container direction="column" spacing={0} className={classes.root} style={{ marginTop: 32 }}>
      <Grid item className={classes.sectionHeader}>
        <DazzedHeading20>
          {t('pages.accountSettings.advancedActions.autochartSettings.title', 'Autochart Tooth Numbering')}
        </DazzedHeading20>
      </Grid>
      <Grid item>
        <DazzedParagraph14 strong>
          {t(
            'pages.accountSettings.advancedActions.autochartSettings.description',
            'Choose which tooth numbering system to display in Autochart.'
          )}
        </DazzedParagraph14>
      </Grid>
      <Grid item style={{ marginTop: 8 }}>
        <GrinRadioGroup
          options={options}
          value={selected}
          setValue={setSelected}
          labelClassName={classes.optionLabel}
          size="small"
          isDisabled={false}
          row={false}
        />
      </Grid>
      <Grid item style={{ marginTop: 12 }}>
        <PrimaryButton
          isLoading={isLoading}
          label={t('general.save')}
          disabled={!selected || selected === (savedSystem || effectiveSystem)}
          onClick={handleSave}
        />
      </Grid>
    </Grid>
  )
}

export default AutochartSettings
